
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import {
  Panel,
  PanelHeader,
  PanelBody,
  PanelSection,
  PanelFooter,
  SegmentedControl,
  Flex,
  FlexItem,
  FlexJustify,
  Select,
  Title,
  Size,
  Button,
  Tier,
  Category,
  Table,
  Pagination,
  Margin,
} from '@drawbotics/react-drylus';
import sv from '@drawbotics/drylus-style-vars';
import Playground, { PropsTable } from '~/components/Playground';
export const Content = () => {
  return <div style={{
    width: '100%',
    height: '200px',
    background: 'orange',
    padding: sv.defaultPadding
  }}>
      Some content goes here
    </div>;
};

const layoutProps = {
  Content
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<h1>{`Panel`}</h1>
<p>{`Usage`}</p>
<pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { Panel, PanelHeader, PanelBody, PanelSection, PanelFooter } from '@drawbotics/react-drylus';
`}</code></pre>
<p>{`Where `}<inlineCode parentName="p">{`PanelBody`}</inlineCode>{` takes `}<inlineCode parentName="p">{`PanelSection`}</inlineCode>{` as children. See example:`}</p>

<Playground __position={0} __code={`<Panel
  header={
    <PanelHeader>
      <Flex justify={FlexJustify.SPACE_BETWEEN}>
        <FlexItem flex>
          <Title size={4} noMargin>Header of the panel</Title>
        </FlexItem>
        <FlexItem>
          <Margin size={{ right: Size.SMALL }}>
            <Select
              placeholder="Filter 1..."
              options={[
                { label: 'Option 1', value: '1' },
                { label: 'Option 2', value: '2' },
              ]}
            />
          </Margin>
        </FlexItem>
        <FlexItem>
          <Select
            placeholder="Another action..."
            options={[
              { label: 'Option 1', value: '1' },
              { label: 'Option 2', value: '2' },
            ]}
          />
        </FlexItem>
      </Flex>
    </PanelHeader>
  }
  body={
    <PanelBody>
      <PanelSection title="Revenue">
        <Content />
      </PanelSection>
      <PanelSection title="Growth">
        <Content />
      </PanelSection>
    </PanelBody>
  }
  footer={
    <PanelFooter>
      <Flex justify={FlexJustify.END}>
        <FlexItem>
          <Margin size={{ right: Size.SMALL }}>
            <Button tier={Tier.TERTIARY}>Tertiary action</Button>
          </Margin>
        </FlexItem>
        <FlexItem>
          <Margin size={{ right: Size.SMALL }}>
            <Button tier={Tier.SECONDARY}>Secondary action</Button>
          </Margin>
        </FlexItem>
        <FlexItem>
          <Button category={Category.BRAND}>Primary action</Button>
        </FlexItem>
      </Flex>
    </PanelFooter>
  } />`} component={Panel} mdxType="Playground">
  <Panel header={<PanelHeader mdxType="PanelHeader">
        <Flex justify={FlexJustify.SPACE_BETWEEN} mdxType="Flex">
          <FlexItem flex mdxType="FlexItem">
            <Title size={4} noMargin mdxType="Title">Header of the panel</Title>
          </FlexItem>
          <FlexItem mdxType="FlexItem">
            <Margin size={{
              right: Size.SMALL
            }} mdxType="Margin">
              <Select placeholder="Filter 1..." options={[{
                label: 'Option 1',
                value: '1'
              }, {
                label: 'Option 2',
                value: '2'
              }]} mdxType="Select" />
            </Margin>
          </FlexItem>
          <FlexItem mdxType="FlexItem">
            <Select placeholder="Another action..." options={[{
              label: 'Option 1',
              value: '1'
            }, {
              label: 'Option 2',
              value: '2'
            }]} mdxType="Select" />
          </FlexItem>
        </Flex>
      </PanelHeader>} body={<PanelBody mdxType="PanelBody">
        <PanelSection title="Revenue" mdxType="PanelSection">
          <Content mdxType="Content" />
        </PanelSection>
        <PanelSection title="Growth" mdxType="PanelSection">
          <Content mdxType="Content" />
        </PanelSection>
      </PanelBody>} footer={<PanelFooter mdxType="PanelFooter">
        <Flex justify={FlexJustify.END} mdxType="Flex">
          <FlexItem mdxType="FlexItem">
            <Margin size={{
              right: Size.SMALL
            }} mdxType="Margin">
              <Button tier={Tier.TERTIARY} mdxType="Button">Tertiary action</Button>
            </Margin>
          </FlexItem>
          <FlexItem mdxType="FlexItem">
            <Margin size={{
              right: Size.SMALL
            }} mdxType="Margin">
              <Button tier={Tier.SECONDARY} mdxType="Button">Secondary action</Button>
            </Margin>
          </FlexItem>
          <FlexItem mdxType="FlexItem">
            <Button category={Category.BRAND} mdxType="Button">Primary action</Button>
          </FlexItem>
        </Flex>
      </PanelFooter>} mdxType="Panel" />
</Playground>
<p>{`Both `}<inlineCode parentName="p">{`PanelHeader`}</inlineCode>{` and `}<inlineCode parentName="p">{`PanelFooter`}</inlineCode>{` don't have any styles, they are simply containers. You should build your own content, and normally we use that space for actions (on either or both sides, or that take the full width).`}</p>
<h4>{`PanelHeader`}</h4>
<p><inlineCode parentName="p">{`PanelBody`}</inlineCode>{` and `}<inlineCode parentName="p">{`PanelFooter`}</inlineCode>{` take identical props.`}</p>
<PropsTable component={PanelHeader} mdxType="PropsTable" />
<h4>{`PanelSection`}</h4>
<PropsTable component={PanelSection} mdxType="PropsTable" />
<h3>{`Common examples`}</h3>
<p>{`Here is an example found in the Backoffice: the main content is a table, and we have some pagination at the bottom to navigate the different pages.`}</p>
<Playground __position={3} __code={`<Panel
  body={
    <PanelBody noPadding>
      <PanelSection>
        <Table header={['name', 'height', 'age', 'children']} data={[{
          name: 'Mike',
          age: 35,
          height: '180cm',
          children: 2,
        }, {
          name: 'Claire',
          age: 20,
          height: '170cm',
          children: 1,
        }, {
          name: 'Johnny',
          age: 5,
          height: '120cm',
          children: 0,
        }, {
          name: 'Laura',
          age: 10,
          height: '150cm',
          children: 0,
        }, {
          name: 'Timmy',
          age: 3,
          height: '70cm',
          children: 0,
        }]} />
      </PanelSection>
    </PanelBody>
  }
  footer={
    <PanelFooter>
      <Flex justify={FlexJustify.END}>
        <FlexItem>
          <Pagination pages={5} value={1} />
        </FlexItem>
      </Flex>
    </PanelFooter>
} />`} mdxType="Playground">
  <Panel body={<PanelBody noPadding mdxType="PanelBody">
        <PanelSection mdxType="PanelSection">
          <Table header={['name', 'height', 'age', 'children']} data={[{
            name: 'Mike',
            age: 35,
            height: '180cm',
            children: 2
          }, {
            name: 'Claire',
            age: 20,
            height: '170cm',
            children: 1
          }, {
            name: 'Johnny',
            age: 5,
            height: '120cm',
            children: 0
          }, {
            name: 'Laura',
            age: 10,
            height: '150cm',
            children: 0
          }, {
            name: 'Timmy',
            age: 3,
            height: '70cm',
            children: 0
          }]} mdxType="Table" />
        </PanelSection>
      </PanelBody>} footer={<PanelFooter mdxType="PanelFooter">
        <Flex justify={FlexJustify.END} mdxType="Flex">
          <FlexItem mdxType="FlexItem">
            <Pagination pages={5} value={1} mdxType="Pagination" />
          </FlexItem>
        </Flex>
      </PanelFooter>} mdxType="Panel" />
</Playground>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;